import { BUILT_IN_APPS_BASE_URL } from "./triggerApps";

/** Default stub shown when adding Run Python — user/copilot replaces with real logic. */
export const WORKLOAD_CODE_PYTHON_STUB = `# Variables from Input Data are available as body / input_data (dict).
# Set output to the value this step should return.
output = {
    "ok": True,
    "echo": body,
}
`;

/** Fields for runPython (matches production apps/workload-code formData). */
export const WORKLOAD_CODE_RUN_PYTHON_FIELDS = [
  {
    schema: {
      type: "array",
      title: "Input Data",
      description: "Add the variables you want to reference from your Python code",
      items: {
        type: "object",
        properties: {
          key: {
            type: "string",
            title: "Key",
            noDataPicker: true,
          },
          value: {
            type: "string",
            title: "Value",
          },
        },
        required: ["key"],
      },
      default: [{ key: "", value: "" }],
    },
    name: "inputs",
    title: "Input Data",
    description: "Variables available to your Python code",
    in: "path",
  },
  {
    schema: {
      type: "string",
    },
    name: "python_code",
    title: "Python Code",
    description:
      "Stable Python 3.11 for this tool. The agent fills Input Data each run — do not rewrite this code at runtime.",
    in: "path",
  },
];

export const WORKLOAD_CODE_RUN_PYTHON_ACTION = {
  key: "runPython",
  title: "Run Python",
  description:
    "Run deterministic Python for transforms, parsing, and mapping. Keep python_code fixed; pass per-run data via inputs.",
  customForm: {
    custom: true,
  },
  fields: WORKLOAD_CODE_RUN_PYTHON_FIELDS,
  dynamicForm: null,
  body: "{}",
  url_params: "{}",
  method: "",
  url: "",
};

/** Fields for browseWeb (AgentCore Browser — no integration Lambda). */
export const WORKLOAD_BROWSER_BROWSE_WEB_FIELDS = [
  {
    schema: {
      type: "string",
      title: "Session ID",
      description:
        "Leave empty to start a new browser session. Reuse sessionId from a previous Browser step output.",
    },
    name: "sessionId",
    title: "Session ID",
    description:
      "Empty to start a new session; reuse prior Browser step sessionId within a run.",
    in: "path",
    value: "Let agent pick value",
  },
  {
    schema: {
      type: "object",
      title: "Browser Task",
      description:
        "Optional startUrl plus actions[] (navigate, click, type, scroll, wait, extract, screenshot).",
      properties: {
        startUrl: { type: "string", title: "Start URL" },
        actions: {
          type: "array",
          title: "Actions",
          items: { type: "object" },
        },
      },
    },
    name: "Browser Task",
    title: "Browser Task",
    description:
      "Public HTTPS browsing: navigate/click/type/scroll/extract. Agent fills this each run.",
    in: "path",
    value: "Let agent pick value",
  },
];

export const WORKLOAD_BROWSER_BROWSE_WEB_ACTION = {
  key: "browseWeb",
  title: "Browse the Web",
  description:
    "Navigate, click, type, scroll, and extract text from public HTTPS pages (prices, scores, research).",
  customForm: {
    custom: true,
  },
  fields: WORKLOAD_BROWSER_BROWSE_WEB_FIELDS,
  dynamicForm: null,
  body: "{}",
  url_params: "{}",
  method: "",
  url: "",
};

/**
 * Hardcoded built-in Utility action apps for the agent tool picker.
 * Synthetic fallback when the live catalog is missing the app; formDataEndpoint still points at production.
 */
/** Phase 2 Skill Consumer — only useful when agent.policy.skills_consumer_enabled. */
export const WORKLOAD_SKILLS_ACTIONS = {
  "skills.search": {
    key: "skills.search",
    title: "Search skills",
    description:
      "Discover approved Workload skills (name + description + pinned version). Does not grant tools.",
    customForm: { custom: true },
    fields: [
      {
        schema: { type: "string", title: "Query" },
        name: "query",
        title: "Query",
        description: "Natural-language or keyword match against skill descriptions",
        in: "path",
        value: "Let agent pick value",
      },
    ],
    dynamicForm: null,
    body: "{}",
    url_params: "{}",
    method: "",
    url: "",
  },
  "skills.get": {
    key: "skills.get",
    title: "Get skill file",
    description:
      "Load SKILL.md (or another package file) for an exact skill_id + version. Pin version for the whole run.",
    customForm: { custom: true },
    fields: [
      {
        schema: { type: "string" },
        name: "skill_id",
        title: "Skill ID",
        description: "e.g. global/build-workload-page",
        in: "path",
        value: "Let agent pick value",
      },
      {
        schema: { type: "integer" },
        name: "version",
        title: "Pinned version",
        description: "Exact approved version from skills.search — required to avoid mid-run drift",
        in: "path",
        value: "Let agent pick value",
      },
      {
        schema: { type: "string", default: "SKILL.md" },
        name: "path",
        title: "File path",
        description: "SKILL.md or references/… / assets/…",
        in: "path",
        value: "SKILL.md",
      },
    ],
    dynamicForm: null,
    body: "{}",
    url_params: "{}",
    method: "",
    url: "",
  },
};

export const WORKLOAD_PAGES_ACTIONS = {
  "pages.create": {
    key: "pages.create",
    title: "Create page",
    description: "Create a private Workload Page (tenant/slug).",
    customForm: { custom: true },
    fields: [
      { schema: { type: "string" }, name: "slug", title: "Slug", in: "path", value: "Let agent pick value" },
      { schema: { type: "string" }, name: "title", title: "Title", in: "path", value: "Let agent pick value" },
    ],
    dynamicForm: null,
    body: "{}",
    url_params: "{}",
    method: "",
    url: "",
  },
  "pages.update_draft": {
    key: "pages.update_draft",
    title: "Update page draft",
    description: "Replace draft canvas JSON for a page.",
    customForm: { custom: true },
    fields: [
      { schema: { type: "string" }, name: "slug", title: "Slug", in: "path", value: "Let agent pick value" },
      { schema: { type: "object" }, name: "canvas", title: "Canvas", in: "path", value: "Let agent pick value" },
    ],
    dynamicForm: null,
    body: "{}",
    url_params: "{}",
    method: "",
    url: "",
  },
  "pages.validate": {
    key: "pages.validate",
    title: "Validate page draft",
    description: "Publish-grade validation without publishing.",
    customForm: { custom: true },
    fields: [
      { schema: { type: "string" }, name: "slug", title: "Slug", in: "path", value: "Let agent pick value" },
    ],
    dynamicForm: null,
    body: "{}",
    url_params: "{}",
    method: "",
    url: "",
  },
  "pages.preview": {
    key: "pages.preview",
    title: "Preview page draft",
    description: "Load draft preview. Do not publish without user_approved.",
    customForm: { custom: true },
    fields: [
      { schema: { type: "string" }, name: "slug", title: "Slug", in: "path", value: "Let agent pick value" },
    ],
    dynamicForm: null,
    body: "{}",
    url_params: "{}",
    method: "",
    url: "",
  },
  "pages.publish": {
    key: "pages.publish",
    title: "Publish page",
    description:
      "Publish a draft. Do not invent user_approved=true — Preview Approve / human gate sets it at execute time.",
    customForm: { custom: true },
    fields: [
      { schema: { type: "string" }, name: "slug", title: "Slug", in: "path", value: "Let agent pick value" },
      {
        schema: { type: "boolean" },
        name: "user_approved",
        title: "User approved",
        description:
          "Leave for the human gate. Preview Approve injects true at execute — the model must not invent it.",
        in: "path",
        value: "Let agent pick value",
      },
      {
        schema: { type: "array" },
        name: "initial_snapshots",
        title: "Initial snapshots",
        in: "path",
        value: "Let agent pick value",
      },
    ],
    dynamicForm: null,
    body: "{}",
    url_params: "{}",
    method: "",
    url: "",
  },
};

export const BUILT_IN_ACTION_PICKER_APPS = [
  {
    name: "Workload Code",
    app: "workload-code",
    slug: "workload-code",
    icon: "https://cdn.workload.co/wp-content/uploads/new-logo-only-512-512.png",
    auth: false,
    formDataEndpoint: `${BUILT_IN_APPS_BASE_URL}/workload-code`,
    category: "Utility",
    trigger: false,
    action: true,
    /** Inline actions so the editor works even if GET formDataEndpoint fails. */
    actions: {
      runPython: WORKLOAD_CODE_RUN_PYTHON_ACTION,
    },
  },
  {
    name: "Browser",
    app: "workload-browser",
    slug: "workload-browser",
    icon: "https://cdn.workload.co/wp-content/uploads/new-logo-only-512-512.png",
    auth: false,
    formDataEndpoint: "",
    category: "Utility",
    trigger: false,
    action: true,
    actions: {
      browseWeb: WORKLOAD_BROWSER_BROWSE_WEB_ACTION,
    },
  },
  {
    name: "Workload Skills",
    app: "workload-skills",
    slug: "workload-skills",
    icon: "https://cdn.workload.co/wp-content/uploads/new-logo-only-512-512.png",
    auth: false,
    formDataEndpoint: "",
    category: "Utility",
    trigger: false,
    action: true,
    actions: WORKLOAD_SKILLS_ACTIONS,
  },
  {
    name: "Workload Pages",
    app: "workload-pages",
    slug: "workload-pages",
    icon: "https://cdn.workload.co/wp-content/uploads/new-logo-only-512-512.png",
    auth: false,
    formDataEndpoint: "",
    category: "Utility",
    trigger: false,
    action: true,
    actions: WORKLOAD_PAGES_ACTIONS,
  },
];

export const isBuiltInActionApp = (appSlug) => {
  const s = String(appSlug || "")
    .toLowerCase()
    .trim();
  return BUILT_IN_ACTION_PICKER_APPS.some(
    (a) => String(a.app || a.slug || "").toLowerCase() === s
  );
};

/** PoC allowlist — mirrors backend agent_skills_consumer._HARDCODED_SKILLS_USER_IDS. */
export const SKILLS_CONSUMER_ALLOWLISTED_USER_IDS = new Set([
  "60ff9c9d56f7e93262de5fd1",
]);

export const isSkillsConsumerUser = (userId) => {
  const uid = String(userId || "").trim();
  return Boolean(uid && SKILLS_CONSUMER_ALLOWLISTED_USER_IDS.has(uid));
};

const _skillConsumerActionId = (actionKey) => {
  const k = String(actionKey || "")
    .toLowerCase()
    .replace(/\./g, "_");
  return `action_${k}`;
};

/** Default agent.actions rows for skills/pages consumer (stable action_ids). */
export const defaultSkillsConsumerAgentActions = () => {
  const icon =
    "https://cdn.workload.co/wp-content/uploads/new-logo-only-512-512.png";
  const rows = [];
  const push = (app, appLabel, catalog) => {
    Object.values(catalog || {}).forEach((spec) => {
      if (!spec || !spec.key) return;
      const fields = (spec.fields || []).map((f) => ({
        name: f.name,
        value: f.value != null ? f.value : "Let agent pick value",
        in: f.in || "path",
      }));
      rows.push({
        action_id: _skillConsumerActionId(spec.key),
        type: "run_workflow_step",
        label: `${appLabel}: ${spec.title || spec.key}`,
        app,
        action_key: spec.key,
        action_label: spec.title || spec.key,
        app_label: appLabel,
        app_icon: icon,
        url: "",
        fields,
        config: {
          app,
          action: spec.key,
          credential: "",
          authname: "",
          fields,
        },
      });
    });
  };
  push("workload-skills", "Workload Skills", WORKLOAD_SKILLS_ACTIONS);
  push("workload-pages", "Workload Pages", WORKLOAD_PAGES_ACTIONS);
  return rows;
};

/**
 * When policy opts in (or allowlisted user), ensure skills/pages tools exist on the agent
 * so Preview Approve can execute them (brain already injects them for planning).
 */
export const ensureSkillsConsumerActionsOnAgent = (agent, { userId } = {}) => {
  if (!agent || typeof agent !== "object") return agent;
  const policy =
    agent.policy && typeof agent.policy === "object" ? agent.policy : {};
  const optedIn = policy.skills_consumer_enabled === true;
  const allowlisted = isSkillsConsumerUser(
    userId || agent.created_by_user_id || agent.user_id
  );
  if (!optedIn && !allowlisted) return agent;

  const actions = Array.isArray(agent.actions) ? [...agent.actions] : [];
  const have = new Set(
    actions
      .map((a) =>
        String(a?.action_key || a?.config?.action || "")
          .toLowerCase()
          .trim()
      )
      .filter(Boolean)
  );
  let changed = false;
  for (const row of defaultSkillsConsumerAgentActions()) {
    const key = String(row.action_key || "").toLowerCase();
    if (!key || have.has(key)) continue;
    actions.push(row);
    have.add(key);
    changed = true;
  }
  const nextPolicy = optedIn
    ? policy
    : { ...policy, skills_consumer_enabled: true };
  if (!changed && optedIn) return agent;
  return {
    ...agent,
    actions,
    policy: nextPolicy,
  };
};

export const findBuiltInActionApp = (appSlug) => {
  const s = String(appSlug || "")
    .toLowerCase()
    .trim();
  if (!s) return null;
  return (
    BUILT_IN_ACTION_PICKER_APPS.find(
      (a) =>
        String(a.app || "").toLowerCase() === s ||
        String(a.slug || "").toLowerCase() === s
    ) || null
  );
};

/** Merge catalog action apps with built-ins (built-in wins on slug collision for auth/category/inline actions). */
export const mergeActionAppsWithBuiltIns = (catalogActionApps = []) => {
  const list = Array.isArray(catalogActionApps) ? [...catalogActionApps] : [];
  const bySlug = new Map();
  list.forEach((app, idx) => {
    const slug = String(app?.app || app?.slug || app?.passportApp || "")
      .toLowerCase()
      .trim();
    if (slug) bySlug.set(slug, idx);
  });
  BUILT_IN_ACTION_PICKER_APPS.forEach((builtIn) => {
    const slug = String(builtIn.app || builtIn.slug || "")
      .toLowerCase()
      .trim();
    if (!slug) return;
    if (bySlug.has(slug)) {
      const idx = bySlug.get(slug);
      list[idx] = {
        ...list[idx],
        ...builtIn,
        // Prefer live endpoint if catalog already has one.
        formDataEndpoint: list[idx].formDataEndpoint || builtIn.formDataEndpoint,
        actions: builtIn.actions || list[idx].actions,
        auth: false,
        category: builtIn.category || list[idx].category || "Utility",
      };
    } else {
      list.unshift(builtIn);
    }
  });
  return list;
};
